import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/api/main/client'

import { ApiError, QueryParams } from '@/api/types'
import {
  keepPreviousData,
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'
import { AxiosRequestConfig } from 'axios'

// GET - Query Hook
export function useApiQuery<TData, TError = ApiError>(
  queryKey: QueryKey,
  endpoint: string,
  params?: QueryParams,
  options?: Omit<UseQueryOptions<TData, TError, TData>, 'queryKey' | 'queryFn'>,
  axiosConfig?: Omit<AxiosRequestConfig, 'params'>
) {
  return useQuery<TData, TError>({
    queryKey,
    queryFn: async () => {
      return apiGet<TData>(endpoint, params, axiosConfig)
    },
    placeholderData: keepPreviousData,
    retry: 1,
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    enabled: options?.enabled ?? true,
    ...options,
  })
}

// POST - Mutation Hook
export function useApiPost<TData, TVariables, TError = ApiError>(
  endpoint: string,
  options?: Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>,
  axiosConfig?: AxiosRequestConfig
) {
  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables) => apiPost<TData>(endpoint, variables, axiosConfig),
    ...options,
  })
}

// PUT - Mutation Hook
export function useApiPut<TData, TVariables, TError = ApiError>(
  endpoint: string,
  options?: Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>,
  axiosConfig?: AxiosRequestConfig
) {
  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables) => await apiPut<TData>(endpoint, variables, axiosConfig),
    ...options,
  })
}

// PATCH - Mutation Hook
export function useApiPatch<TData, TVariables, TError = ApiError>(
  endpoint: string,
  options?: Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>,
  axiosConfig?: AxiosRequestConfig
) {
  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables) => await apiPatch<TData>(endpoint, variables, axiosConfig),
    ...options,
  })
}

// DELETE - Mutation Hook
export function useApiDelete<TData, TVariables = string, TError = ApiError>(
  endpoint: string,
  options?: Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>,
  axiosConfig?: AxiosRequestConfig
) {
  return useMutation<TData, TError, TVariables>({
    mutationFn: async (id) => {
      const finalEndpoint = typeof id === 'string' ? `${endpoint}/${id}` : endpoint
      return await apiDelete<TData>(finalEndpoint, axiosConfig)
    },
    ...options,
  })
}
